import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Badge,
  Spinner,
  useToast,
  Button,
  HStack,
  useDisclosure,
  Input,
  Select,
  Tooltip,
  IconButton,
} from '@chakra-ui/react';
import { AddIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useSortBy, useTable, usePagination } from 'react-table';
import * as XLSX from 'xlsx';
import BlockedUnregisteredUsersApi from '../../../../api/blockedUnregisteredUsers';
import BlockedUnregisteredUserFormModal from './BlockedUnregisteredUserFormModal';
import { prettifyDate } from '../../../../functions/dateUtils';
import { useCompaniesOptions } from '../../../../hooks/useCompaniesOptions';

const PAGE_SIZE = 20;

const Highlight = ({ text, term }) => {
  if (!term || !text) return <>{text || '—'}</>;
  const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = String(text).split(regex);
  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} style={{ background: '#fef08a', color: 'inherit', borderRadius: '2px' }}>
            {part}
          </mark>
        ) : (
          part
        )
      )}
    </>
  );
};

const BlockedUnregisteredUsersModal = ({ isOpen, onClose }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState('ALL');
  const searchTimer = useRef(null);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const toast = useToast();
  const { companiesOptions, companiesIdCache } = useCompaniesOptions({ showAll: true });

  const {
    isOpen: isFormOpen,
    onOpen: onFormOpen,
    onClose: onFormClose,
  } = useDisclosure();

  const fetchData = async (searchValue = search) => {
    setLoading(true);
    try {
      const res = await BlockedUnregisteredUsersApi.getAll(searchValue);
      setData(res.data || []);
    } catch (err) {
      toast({ title: 'שגיאה בטעינת נתונים', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchData('');
      setSearch('');
      setSelectedCompanyId('ALL');
    }
  }, [isOpen]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      fetchData(value);
    }, 300);
  };

  const filteredData = useMemo(() => {
    if (selectedCompanyId === 'ALL') return data;
    return data.filter((row) => String(row.companyId) === String(selectedCompanyId));
  }, [data, selectedCompanyId]);

  const handleExport = () => {
    const rows = filteredData.map((row) => ({
      'שם פרטי': row.firstName || '',
      'שם משפחה': row.lastName || '',
      'טלפון': row.phoneNumber || '',
      'תעודת זהות': row.govId || '',
      'מזהה עובד': row.empId || '',
      'חברה': companiesIdCache[row.companyId] || row.companyId || '',
      'נוצר ב': row.createdAt ? prettifyDate(row.createdAt) : '',
      'סטטוס': row.isRegistered ? `רשום${row.registeredName ? ' - ' + row.registeredName : ''}` : 'לא רשום',
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'חסומים לא רשומים');
    XLSX.writeFile(workbook, 'blocked_unregistered_users.xlsx');
  };

  const columns = useMemo(
    () => [
      {
        Header: 'שם פרטי',
        accessor: 'firstName',
        Cell: ({ value }) => <Highlight text={value} term={search} />,
      },
      {
        Header: 'שם משפחה',
        accessor: 'lastName',
        Cell: ({ value }) => <Highlight text={value} term={search} />,
      },
      {
        Header: 'טלפון',
        accessor: 'phoneNumber',
        Cell: ({ value }) => <Highlight text={value} term={search} />,
      },
      {
        Header: 'תעודת זהות',
        accessor: 'govId',
        Cell: ({ value }) => <Highlight text={value} term={search} />,
      },
      {
        Header: 'מזהה עובד',
        accessor: 'empId',
        Cell: ({ value }) => <Highlight text={value} term={search} />,
      },
      {
        Header: 'חברה',
        accessor: 'companyId',
        Cell: ({ value }) => companiesIdCache[value] || value || '—',
      },
      {
        Header: 'נוצר ב',
        accessor: 'createdAt',
        Cell: ({ value }) => (value ? prettifyDate(value) : '—'),
      },
      {
        Header: 'סטטוס',
        accessor: 'isRegistered',
        Cell: ({ row }) =>
          row.original.isRegistered ? (
            <Tooltip label={row.original.registeredName || ''} hasArrow>
              <Badge colorScheme="green">רשום</Badge>
            </Tooltip>
          ) : (
            <Badge colorScheme="gray">לא רשום</Badge>
          ),
      },
    ],
    [companiesIdCache, search]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    state: { pageIndex },
  } = useTable(
    { columns, data: filteredData, initialState: { pageSize: PAGE_SIZE } },
    useSortBy,
    usePagination
  );

  // Reset to page 0 when search or company filter changes
  useEffect(() => {
    gotoPage(0);
  }, [filteredData]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="6xl">
        <ModalOverlay />
        <ModalContent maxW="90%" h="85vh" display="flex" flexDirection="column">
          <ModalCloseButton position="absolute" left="8px" right="auto" top="8px" zIndex="3" />
          <ModalHeader flexShrink={0}>
            <Flex align="center" justify="space-between" flexWrap="wrap" gap={3}>
              <HStack spacing={3}>
                <Text>משתמשים חסומים לא רשומים</Text>
                <Badge colorScheme="red" fontSize="md" px={2}>
                  {filteredData.length}
                </Badge>
              </HStack>
              <HStack spacing={2}>
                <Button leftIcon={<AddIcon />} colorScheme="red" size="sm" onClick={onFormOpen}>
                  צור משתמש חסום
                </Button>
                <Button size="sm" variant="outline" onClick={handleExport}>
                  ייצוא לאקסל
                </Button>
              </HStack>
            </Flex>
          </ModalHeader>
          <ModalBody pb={2} display="flex" flexDirection="column" overflow="hidden">
            <HStack mb={4} spacing={3} flexShrink={0}>
              <Input
                placeholder="חיפוש לפי שם, טלפון, ת.ז, מזהה עובד..."
                value={search}
                onChange={handleSearchChange}
                maxW="350px"
              />
              <Select
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(e.target.value)}
                maxW="220px"
              >
                {companiesOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </HStack>

            <Box flex="1" overflowY="auto" overflowX="auto">
              {loading ? (
                <Flex justify="center" align="center" h="100%">
                  <Spinner size="xl" />
                </Flex>
              ) : filteredData.length === 0 ? (
                <Flex justify="center" align="center" h="100%">
                  <Text color="gray.500">אין תוצאות</Text>
                </Flex>
              ) : (
                <Table {...getTableProps()} variant="simple" color="gray.500">
                  <Thead position="sticky" top={0} zIndex={1} bg="white">
                    {headerGroups.map((headerGroup, i) => (
                      <Tr {...headerGroup.getHeaderGroupProps()} key={i}>
                        {headerGroup.headers.map((column, j) => (
                          <Th
                            {...column.getHeaderProps(column.getSortByToggleProps())}
                            key={j}
                            borderColor={borderColor}
                            pe="10px"
                            cursor="pointer"
                          >
                            <Flex align="center" fontSize="xs" color="gray.400">
                              {column.render('Header')}
                              {column.isSorted ? (column.isSortedDesc ? ' ▼' : ' ▲') : ''}
                            </Flex>
                          </Th>
                        ))}
                      </Tr>
                    ))}
                  </Thead>
                  <Tbody {...getTableBodyProps()}>
                    {page.map((row, i) => {
                      prepareRow(row);
                      return (
                        <Tr {...row.getRowProps()} key={i}>
                          {row.cells.map((cell, j) => (
                            <Td
                              {...cell.getCellProps()}
                              key={j}
                              fontSize="sm"
                              color={textColor}
                              borderColor={borderColor}
                            >
                              {cell.render('Cell')}
                            </Td>
                          ))}
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              )}
            </Box>

            {pageCount > 1 && (
              <Flex justify="center" align="center" pt={3} gap={3} flexShrink={0}>
                <IconButton
                  icon={<ChevronRightIcon />}
                  size="sm"
                  onClick={() => previousPage()}
                  isDisabled={!canPreviousPage}
                  aria-label="previous"
                />
                <Text fontSize="sm" color="gray.500">
                  עמוד {pageIndex + 1} מתוך {pageCount}
                </Text>
                <IconButton
                  icon={<ChevronLeftIcon />}
                  size="sm"
                  onClick={() => nextPage()}
                  isDisabled={!canNextPage}
                  aria-label="next"
                />
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <BlockedUnregisteredUserFormModal
        isOpen={isFormOpen}
        onClose={onFormClose}
        onSuccess={() => fetchData(search)}
      />
    </>
  );
};

export default BlockedUnregisteredUsersModal;
